import * as ents from '@attain-apps/entities'

import type * as me from '../../../me'

/**
 * @see {@link ents.core.admin.User}
 * NOTE: 'cognitoId' is intentionally excluded as it is not intended for
 * frontend use.
 */
export type User = Pick<ents.core.admin.User, 'id' | 'isPlatformAdmin' | 'name'>

export namespace User {
  const ACCESS_FLAGS = ents.core.admin.Access.Flag.WithLabel.get()

  const TENANT_PLATFORM_ADMIN_FLAGS = [
    ents.core.admin.Access.Flag.CanUse,
    ents.core.admin.Access.Flag.CanManageUsers,
    ents.core.admin.Access.Flag.CanConfigureInfo,
    ents.core.admin.Access.Flag.CanCreateChildren,
    ents.core.admin.Access.Flag.CanDeleteUnownedChildren,
    ents.core.admin.Access.Flag.CanGenerateApiKey,
  ] as const

  /**
   * Flags offered on a zone assignment.
   *
   * API keys are bound to a single zone, so the zone assignment is the natural
   * place to grant the permission. A tenant-level grant also covers every zone
   * in that tenant
   */
  const ZONE_FLAGS = [
    ents.core.admin.Access.Flag.CanUse,
    ents.core.admin.Access.Flag.CanGenerateApiKey,
  ] as const

  export interface Filters {
    context?:
      | ents.core.admin.Context.ForTenant
      | ents.core.admin.Context.ForZone
      | ents.core.admin.Context.ForApp
    includePlatformUsers?: boolean
  }

  /**
   * Finds the current user's tenant assignment for an admin context.
   */
  export function findTenant(
    user: me.models.User | undefined,
    context: ents.core.admin.Context | undefined,
  ): me.models.Tenant | undefined {
    return context?.tenantId
      ? user?.tenants.find(tenant => tenant.id === context.tenantId)
      : undefined
  }

  /**
   * Determines whether the current user may manage users for the context's tenant.
   */
  export function canManageUsersForContext(
    user: me.models.User | undefined,
    context: ents.core.admin.Context | undefined,
  ): boolean {
    return (
      Boolean(user?.isPlatformAdmin) ||
      ents.core.admin.Access.allows(
        findTenant(user, context)?.access,
        ents.core.admin.Access.Flag.CanManageUsers,
      )
    )
  }

  /**
   * Deduplicates users by ID.
   */
  export function deduplicate(users: readonly User[]): readonly User[] {
    const usersById = new Map<User['id'], User>()
    for (const user of users) {
      usersById.set(user.id, user)
    }
    return Array.from(usersById.values())
  }

  /**
   * Returns assignment access flags available for the current context.
   */
  export function getAccessFlags({
    activeContext,
    app,
    currentUser,
    selectedUsers,
  }: {
    activeContext: ents.core.admin.Context | undefined
    app: ents.core.admin.App | undefined
    currentUser: me.models.User | undefined
    selectedUsers: readonly User[]
  }): readonly ents.core.admin.Access.Flag.WithLabel[] {
    if (selectedUsers.some(user => user.isPlatformAdmin)) {
      return _withLabels([ents.core.admin.Access.Flag.CanUse])
    }

    if (isTenantContext(activeContext)) {
      return currentUser?.isPlatformAdmin
        ? _withLabels(TENANT_PLATFORM_ADMIN_FLAGS)
        : _withLabels([ents.core.admin.Access.Flag.CanUse])
    }

    if (_isAppContext(activeContext)) {
      const flags: ents.core.admin.Access.Flag[] = [
        ents.core.admin.Access.Flag.CanUse,
      ]
      if (app?.supportsFunctionalityConfiguration === true) {
        flags.push(ents.core.admin.Access.Flag.CanConfigureFunctionality)
      }
      if (app?.supportsScenarios) {
        flags.push(
          ents.core.admin.Access.Flag.CanCreateChildren,
          ents.core.admin.Access.Flag.CanDeleteUnownedChildren,
        )
      }
      return _withLabels(flags)
    }

    if (_isZoneContext(activeContext)) {
      return _withLabels(ZONE_FLAGS)
    }

    return _withLabels([ents.core.admin.Access.Flag.CanUse])
  }

  /**
   * Identifies tenant-level admin contexts.
   */
  export function isTenantContext(
    context: ents.core.admin.Context | undefined,
  ): context is ents.core.admin.Context.ForTenant {
    return context !== undefined && !('zoneId' in context)
  }

  /**
   * Limits access to the available flags for the current assignment context.
   */
  export function limitAccessToFlags(
    access: ents.core.admin.Access,
    flags: readonly ents.core.admin.Access.Flag.WithLabel[],
  ): ents.core.admin.Access {
    return ents.core.admin.Access.intersection(
      access,
      ents.core.admin.Access.union(...flags.map(flag => flag.value)),
    )
  }

  /**
   * Identifies zone-level admin contexts — a zone with no app or scenario
   * narrowing it further.
   */
  function _isZoneContext(
    context: ents.core.admin.Context | undefined,
  ): context is ents.core.admin.Context.ForZone {
    return (
      context !== undefined &&
      'zoneId' in context &&
      !('appId' in context) &&
      !('scenarioId' in context)
    )
  }

  /**
   * Identifies app-level admin contexts.
   */
  function _isAppContext(
    context: ents.core.admin.Context | undefined,
  ): context is ents.core.admin.Context.ForApp {
    return (
      context !== undefined && 'appId' in context && !('scenarioId' in context)
    )
  }

  /**
   * Returns labeled access flags for the given flag values.
   */
  function _withLabels(
    flags: readonly ents.core.admin.Access.Flag[],
  ): readonly ents.core.admin.Access.Flag.WithLabel[] {
    return ACCESS_FLAGS.filter(flag => flags.includes(flag.value))
  }
}
